// «Nuevo lead»: alta manual (POST /admin/sales/leads/, org del JWT). Si el
// teléfono ya existía no se duplica: el backend completa el registro y avisa.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, UserPlus, X } from 'lucide-react';

import { createLead, type CreateLeadResult } from '@/modules/leads-import';

export function NewLeadDialog({ onClose, onCreated, portalTarget }: {
  onClose: () => void;
  onCreated: (r: CreateLeadResult) => void;
  portalTarget?: Element | null;
}) {
  const [f, setF] = useState({ name: '', phone: '', email: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const valid = !!(f.name.trim() || f.phone.trim() || f.email.trim()) && (!f.email.trim() || /.+@.+\..+/.test(f.email.trim()));

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    boxRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'Tab' && boxRef.current) {
        const els = Array.from(boxRef.current.querySelectorAll<HTMLElement>('input,textarea,button:not(:disabled)'));
        const first = els[0]; const last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await createLead({
        name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(), message: f.message.trim(), source: 'manual',
      });
      onCreated(r);
    } catch (err: any) {
      setError(err?.status === 403 ? 'Tu rol no puede dar de alta leads. Pídeselo a administración.' : (err?.message || 'No se pudo registrar'));
    } finally { setBusy(false); }
  };

  const input = (k: keyof typeof f, label: string, type = 'text', placeholder = '') => (
    <label className="block">
      <span className="mb-1 block text-[12.5px] font-medium text-foreground">{label}</span>
      <input type={type} value={f[k]} placeholder={placeholder} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}
        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-[14px] text-foreground" />
    </label>
  );

  return createPortal(
    <div className="leads-app fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={onClose}>
      <div ref={boxRef} role="dialog" aria-modal="true" aria-labelledby="la-new-title" data-la-modal onMouseDown={(e) => e.stopPropagation()}
        className="la-in w-full max-w-md rounded-t-2xl border border-border bg-card text-card-foreground shadow-2xl sm:rounded-2xl">
        <form onSubmit={submit}>
          <header className="flex items-center gap-3 border-b border-border px-5 py-4">
            <span className="la-accent-soft la-accent-text grid h-9 w-9 place-items-center rounded-xl"><UserPlus className="h-5 w-5" aria-hidden /></span>
            <div className="min-w-0 flex-1">
              <h2 id="la-new-title" className="text-[16px] font-semibold">Nuevo lead</h2>
              <p className="text-[12.5px] text-muted-foreground">Con nombre, teléfono o correo basta.</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Cerrar" className="grid h-9 w-9 place-items-center rounded-xl text-muted-foreground hover:bg-muted"><X className="h-5 w-5" /></button>
          </header>
          <div className="space-y-3 px-5 py-4">
            {input('name', 'Nombre', 'text', 'María López')}
            <div className="grid gap-3 sm:grid-cols-2">
              {input('phone', 'Teléfono', 'tel', '444 123 4567')}
              {input('email', 'Correo', 'email', 'maria@correo.com')}
            </div>
            <label className="block">
              <span className="mb-1 block text-[12.5px] font-medium text-foreground">¿Qué busca?</span>
              <textarea rows={3} value={f.message} onChange={(e) => setF((x) => ({ ...x, message: e.target.value }))}
                placeholder="Cotización para un grupo de 12 el próximo mes"
                className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-[14px] text-foreground" />
            </label>
            {error && <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive">{error}</p>}
          </div>
          <footer className="flex justify-end gap-2 border-t border-border px-5 py-3">
            <button type="button" onClick={onClose} className="rounded-xl px-3.5 py-2 text-[13.5px] font-medium text-foreground hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={!valid || busy} className="la-btn-brand inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13.5px] font-semibold">
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}Registrar lead
            </button>
          </footer>
        </form>
      </div>
    </div>,
    portalTarget || document.body,
  );
}
